import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  IconButton,
  Link,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
} from '@material-ui/core';
import { Assessment, Build, ChevronRight, Close, EmojiObjects, ExpandMore } from '@material-ui/icons';
import { makeStyles, createStyles } from '@material-ui/styles';
import { Theme } from '@material-ui/core/styles';
import { useEndpointFetch } from '../network/useEndpointFetch';
import { ProcessingView, ErrorView } from './components';
import {
  SetupAnalyseRequest,
  ReviewAnalyseResponse,
  ReviewCategory,
  ReviewSuggestion,
  ReviewLinkKey,
} from './stages/types';
import { useSetupDialogStyles } from './setupDialogStyles';

const LINK_MAP: Record<ReviewLinkKey, { label: string; href: string }> = {
  onboarding: { label: 'Onboarding Settings', href: '/admin/admin-console/onboarding' },
  bulletins: { label: 'Bulletins', href: '/management/bulletins' },
  incidentConfig: { label: 'Incident Configuration', href: '/admin/admin-console/incident-reporting' },
  incidentManagement: { label: 'Incident Management', href: '/management/incident-management' },
  operations: { label: 'Operations Settings', href: '/admin/admin-console/operations' },
  activityReporting: { label: 'Reporting Settings', href: '/admin/admin-console/reporting' },
  roles: { label: 'Roles', href: '/admin/admin-console/roles' },
  reportManagement: { label: 'Report Management', href: '/management/report-management' },
  templates: { label: 'Templates', href: '/admin/admin-console/templates' },
  automations: { label: 'Reminders & Automations', href: '/admin/admin-console/reminders' },
  preferences: { label: 'Preferences', href: '/admin/admin-console/preferences' },
  notifications: { label: 'Notifications & Alerts', href: '/admin/admin-console/notifications' },
  programManagement: { label: 'Program Management', href: '/admin/program-management' },
  activityManagement: { label: 'Activity Management', href: '/management/activity-management' },
};

type Step = 'processing' | 'results' | 'error';

interface ReviewSetupDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete?: (data?: Record<string, unknown>) => void;
  questionnaireAnswers?: Record<string, unknown>;
  completedStages?: string[];
  setupData?: Record<string, unknown>;
  savedResults?: Record<string, unknown> | null;
}

const CATEGORY_ORDER: ReviewCategory[] = ['reporting', 'completeness', 'bestPractice'];

const CATEGORY_CONFIG: Record<ReviewCategory, { label: string; icon: React.ReactElement }> = {
  reporting: { label: 'Reporting', icon: <Assessment /> },
  completeness: { label: 'Setup Completeness', icon: <Build /> },
  bestPractice: { label: 'Best Practices', icon: <EmojiObjects /> },
};

const HARDCODED_SUGGESTIONS: ReviewSuggestion[] = [
  {
    title: 'Communicate with Bulletins',
    description: 'Send announcements and updates to your volunteer community using bulletins.',
    category: 'bestPractice',
    linkKey: 'bulletins',
  },
];

function groupByCategory(suggestions: ReviewSuggestion[]): Map<ReviewCategory, ReviewSuggestion[]> {
  const all = [...suggestions, ...HARDCODED_SUGGESTIONS];
  const grouped = new Map<ReviewCategory, ReviewSuggestion[]>();
  all.forEach((s) => {
    const existing = grouped.get(s.category) || [];
    existing.push(s);
    grouped.set(s.category, existing);
  });
  return grouped;
}

const useLocalStyles = makeStyles((theme: Theme) =>
  createStyles({
    resultsContainer: {
      maxHeight: 450,
      overflow: 'auto',
    },
    expansionPanel: {
      boxShadow: 'none',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${theme.shape.borderRadius}px !important`,
      marginBottom: theme.spacing(1.5),
      backgroundColor: '#fff',
      '&:before': {
        display: 'none',
      },
      '&.Mui-expanded': {
        margin: `0 0 ${theme.spacing(1.5)}px 0`,
      },
    },
    expansionSummary: {
      minHeight: 48,
      padding: '0 16px',
      '&.Mui-expanded': {
        minHeight: 48,
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '& .MuiExpansionPanelSummary-content': {
        margin: '12px 0',
        display: 'flex',
        alignItems: 'center',
        '&.Mui-expanded': {
          margin: '12px 0',
        },
      },
    },
    expansionDetails: {
      padding: '12px 16px 16px 16px',
      flexDirection: 'column',
    },
    sectionIcon: {
      fontSize: 20,
      marginRight: 8,
      color: theme.palette.text.primary,
    },
    sectionLabel: {
      fontWeight: 600,
      fontSize: 14,
      color: theme.palette.text.primary,
      lineHeight: '20px',
    },
    suggestionItem: {
      padding: theme.spacing(1.5, 0),
      borderBottom: `1px solid ${theme.palette.divider}`,
      '&:last-child': {
        borderBottom: 'none',
        paddingBottom: 0,
      },
      '&:first-child': {
        paddingTop: 0,
      },
    },
    suggestionTitle: {
      fontWeight: 600,
      fontSize: '0.875rem',
      color: theme.palette.text.primary,
    },
    suggestionDescription: {
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
      marginTop: theme.spacing(0.5),
    },
    suggestionLink: {
      fontSize: '0.8rem',
      fontWeight: 500,
      marginTop: theme.spacing(0.5),
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
    },
    linkArrow: {
      fontSize: 16,
    },
    recommendationsSection: {
      marginTop: theme.spacing(1.5),
    },
    recommendationsTitle: {
      fontWeight: 600,
      fontSize: '0.9rem',
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(1),
    },
  })
);

function buildSetupSummary(completedStages?: string[], setupData?: Record<string, unknown>): string {
  const parts: string[] = [];

  if (completedStages && completedStages.length > 0) {
    parts.push(`Completed setup stages: ${completedStages.join(', ')}`);
  }

  if (setupData) {
    Object.entries(setupData).forEach(([key, value]) => {
      if (value && typeof value === 'object') {
        parts.push(`${key} stage data: ${JSON.stringify(value)}`);
      }
    });
  }

  return parts.join('\n\n');
}

export function ReviewSetupDialog({
  open,
  onClose,
  onComplete,
  questionnaireAnswers,
  completedStages,
  setupData,
  savedResults: savedResultsProp,
}: ReviewSetupDialogProps) {
  const classes = useSetupDialogStyles();
  const localClasses = useLocalStyles();

  const [step, setStep] = useState<Step>('processing');
  const [results, setResults] = useState<ReviewAnalyseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyseFetch = useEndpointFetch<SetupAnalyseRequest, ReviewAnalyseResponse>('/vm/setup/analyse');

  const setupSummary = buildSetupSummary(completedStages, setupData);

  useEffect(() => {
    if (!open) return;

    if (savedResultsProp?.data) {
      setResults((savedResultsProp.data as unknown) as ReviewAnalyseResponse);
      setStep('results');
      setError(null);
      return;
    }

    setStep('processing');
    setResults(null);
    setError(null);

    analyseFetch
      .run({
        section: 'review',
        files: [],
        description: setupSummary || undefined,
        questionnaireAnswers,
      })
      .then((response) => {
        if (response.ok && response.data?.data) {
          setResults(response.data.data);
          setStep('results');
        } else {
          setError(response.data?.message || 'Failed to analyse configuration');
          setStep('error');
        }
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'An unexpected error occurred');
        setStep('error');
      });
  }, [open]);

  const handleClose = () => {
    onClose();
  };

  const handleComplete = () => {
    if (onComplete) {
      onComplete(results ? { data: results } : undefined);
    }
    handleClose();
  };

  const handleRetry = () => {
    setStep('processing');
    setError(null);
    analyseFetch
      .run({
        section: 'review',
        files: [],
        description: setupSummary || undefined,
        questionnaireAnswers,
      })
      .then((response) => {
        if (response.ok && response.data?.data) {
          setResults(response.data.data);
          setStep('results');
        } else {
          setError(response.data?.message || 'Failed to analyse configuration');
          setStep('error');
        }
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'An unexpected error occurred');
        setStep('error');
      });
  };

  const getSubtitle = () => {
    switch (step) {
      case 'processing':
        return 'Reviewing your configuration...';
      case 'results':
        return 'Here are some suggestions to improve your setup';
      case 'error':
        return 'Something went wrong';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth classes={{ paper: classes.dialogPaper }}>
      <Box className={classes.header}>
        <IconButton className={classes.closeButton} onClick={handleClose} size='small'>
          <Close fontSize='small' />
        </IconButton>
        <Typography className={classes.title}>Review Setup</Typography>
        <Typography className={classes.subtitle}>{getSubtitle()}</Typography>
      </Box>

      <DialogContent className={classes.content}>
        {step === 'processing' && (
          <ProcessingView
            title='Reviewing your setup...'
            subtitle='Analysing your configuration and preparing suggestions'
          />
        )}

        {step === 'results' && results && (
          <Box className={localClasses.resultsContainer}>
            {results.suggestions &&
              results.suggestions.length > 0 &&
              (() => {
                const grouped = groupByCategory(results.suggestions);
                return CATEGORY_ORDER.filter((cat) => grouped.has(cat)).map((cat, index) => {
                  const config = CATEGORY_CONFIG[cat];
                  const items = grouped.get(cat) || [];
                  return (
                    <ExpansionPanel key={cat} defaultExpanded={index === 0} className={localClasses.expansionPanel}>
                      <ExpansionPanelSummary expandIcon={<ExpandMore />} className={localClasses.expansionSummary}>
                        {React.cloneElement(config.icon, {
                          className: localClasses.sectionIcon,
                        })}
                        <Typography className={localClasses.sectionLabel}>
                          {config.label} ({items.length})
                        </Typography>
                      </ExpansionPanelSummary>
                      <ExpansionPanelDetails className={localClasses.expansionDetails}>
                        {items.map((suggestion) => {
                          const linkInfo = suggestion.linkKey ? LINK_MAP[suggestion.linkKey] : undefined;
                          return (
                            <Box key={suggestion.title} className={localClasses.suggestionItem}>
                              <Typography className={localClasses.suggestionTitle}>{suggestion.title}</Typography>
                              <Typography className={localClasses.suggestionDescription}>
                                {suggestion.description}
                              </Typography>
                              {linkInfo && (
                                <Link href={linkInfo.href} className={localClasses.suggestionLink} target='_blank'>
                                  See: {linkInfo.label}
                                  <ChevronRight className={localClasses.linkArrow} />
                                </Link>
                              )}
                            </Box>
                          );
                        })}
                      </ExpansionPanelDetails>
                    </ExpansionPanel>
                  );
                });
              })()}

            {results.recommendations && results.recommendations.length > 0 && (
              <Box className={localClasses.recommendationsSection}>
                <Typography className={localClasses.recommendationsTitle}>Recommendations</Typography>
                <Box component='ul' style={{ margin: 0, paddingLeft: 20 }}>
                  {results.recommendations.map((rec) => (
                    <li key={rec}>
                      <Typography variant='body2' color='textSecondary'>
                        {rec}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}

        {step === 'error' && error && <ErrorView message={error} onRetry={handleRetry} />}
      </DialogContent>

      <DialogActions className={classes.actions}>
        {step === 'processing' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Cancel
            </Button>
            <Box />
          </>
        )}

        {step === 'results' && (
          <>
            <Box />
            <Button className={classes.primaryButton} onClick={handleComplete}>
              Done
            </Button>
          </>
        )}

        {step === 'error' && (
          <>
            <Button className={classes.secondaryButton} onClick={handleClose}>
              Close
            </Button>
            <Button className={classes.primaryButton} onClick={handleRetry}>
              Try Again
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
